import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../supabaseClient'
import { USER_ROLES, security } from '../config/security'
import './Vendas.css'

const PERMISSAO_CONFIGURAR = 'vendas_configurar_metas'

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0)

const formatMoneyInput = (input) => {
  const digits = String(input || '').replace(/\D/g, '')
  if (!digits) return ''
  const value = Number(digits) / 100
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const parseMoney = (str) => {
  const normalized = String(str || '').replace(/\./g, '').replace(',', '.')
  const n = Number(normalized)
  return Number.isFinite(n) ? n : 0
}

const moneyToInput = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const iniciais = (nome) => {
  const partes = String(nome || '').trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return '?'
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase()
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
}

const pad2 = (n) => String(n).padStart(2, '0')

const hojeStr = () => {
  const now = new Date()
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
}

const mesAtualRange = () => {
  const now = new Date()
  const inicioDate = new Date(now.getFullYear(), now.getMonth(), 1)
  const fimDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
  return { inicio: fmt(inicioDate), fim: fmt(fimDate) }
}

const proximoDiaStr = (dateStr) => {
  const [y, m, d] = String(dateStr).split('-').map(Number)
  const dt = new Date(y, (m || 1) - 1, (d || 1) + 1)
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
}

const formatarDataCurta = (dateStr) => {
  const [y, m, d] = String(dateStr || '').split('-')
  if (!y || !m || !d) return ''
  return `${d}/${m}/${y}`
}

const formatarDataHora = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const TITULO_COLUNA = {
  contatos: 'Contatos',
  agendadas: 'Visitas agendadas',
  realizadas: 'Visitas realizadas',
  faturamento: 'Faturamento do dia',
  zona: 'Zona'
}

const STATUS_LEAD = {
  novo_lead: 'Novo lead',
  contato_feito: 'Contato feito',
  proposta_enviada: 'Proposta enviada',
  negociacao: 'Negociação',
  parado: 'Parado',
  fechado_ganhou: 'Fechado (ganhou)',
  fechado_perdeu: 'Fechado (perdido)'
}

const PAGAMENTO_LABEL = {
  avista: 'À vista',
  financiado: 'Financiado'
}

const pct = (valor, meta) => {
  const m = Number(meta) || 0
  if (m <= 0) return valor > 0 ? 100 : 0
  return Math.round((Number(valor) / m) * 100)
}

const tomPorMeta = (valor, meta) => {
  const p = pct(valor, meta)
  if (p >= 100) return 'ok'
  if (p >= 50) return 'atencao'
  return 'baixo'
}

const tomPorFaturamento = (faturamento, zonas) => {
  const fat = Number(faturamento) || 0
  if (fat >= zonas.verde) return 'ok'
  if (fat >= zonas.amarela) return 'atencao'
  return 'baixo'
}

const ZONA_LABEL = { ok: 'Verde', atencao: 'Amarela', baixo: 'Vermelha' }

const metasVazias = () => ({
  meta_contatos: '0',
  meta_visitas_agendadas: '0',
  meta_visitas_realizadas: '0',
  meta_faturamento: moneyToInput(0)
})

const metasDoRegistro = (m) => ({
  meta_contatos: String(m?.meta_contatos ?? 0),
  meta_visitas_agendadas: String(m?.meta_visitas_agendadas ?? 0),
  meta_visitas_realizadas: String(m?.meta_visitas_realizadas ?? 0),
  meta_faturamento: moneyToInput(m?.meta_faturamento ?? 0)
})

const buscarMetas = async () => {
  const completo = await supabase
    .from('especialista_metas')
    .select('id_usuario, meta_contatos, meta_visitas_agendadas, meta_visitas_realizadas, meta_faturamento')
  if (!completo.error) return { rows: completo.data || [], colunaNova: true }
  const msg = completo.error.message || ''
  if (!/meta_faturamento/i.test(msg)) throw completo.error
  const legado = await supabase
    .from('especialista_metas')
    .select('id_usuario, meta_contatos, meta_visitas_agendadas, meta_visitas_realizadas')
  if (legado.error) throw legado.error
  return { rows: legado.data || [], colunaNova: false }
}

const CelulaMeta = ({ valor, meta, tom, dinheiro }) => {
  const p = pct(valor, meta)
  const largura = Math.min(p, 100)
  const metaTexto = dinheiro ? formatBRL(meta) : String(Number(meta) || 0)
  const valorTexto = dinheiro ? formatBRL(valor) : String(valor)
  return (
    <div className="vendas-celula">
      <span className={`vendas-val vendas-tone-${tom}`}>{valorTexto}</span>
      <span className="vendas-meta-label">{p}% de {metaTexto}</span>
      <span className="vendas-bar" aria-hidden="true">
        <span className={`vendas-bar-fill vendas-tone-${tom}`} style={{ width: `${largura}%` }} />
      </span>
    </div>
  )
}

const TabelaDetalhe = ({ colunas, itens }) => (
  <div className="vendas-metas-table-wrap">
    <table className="vendas-metas-table">
      <thead>
        <tr>
          {colunas.map(coluna => <th key={coluna.chave} scope="col">{coluna.titulo}</th>)}
        </tr>
      </thead>
      <tbody>
        {itens.map(item => (
          <tr key={item.id}>
            {colunas.map(coluna => <td key={coluna.chave}>{coluna.valor(item)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const DetalheColuna = ({ detalhe, zonas, dados, vendedores }) => {
  const itens = detalhe.itens || []
  const mostrarVendedor = !detalhe.usuario

  if (detalhe.tipo === 'zona') {
    return (
      <>
        <div className="vendas-zona-resumo">
          <p>Verde desde {formatBRL(zonas.verde)}. Amarela desde {formatBRL(zonas.amarela)}. Abaixo da amarela, a zona é vermelha.</p>
        </div>
        <TabelaDetalhe
          itens={vendedores}
          colunas={[
            { chave: 'nome', titulo: 'Vendedor', valor: (u) => u.nome || 'Sem nome' },
            { chave: 'fat', titulo: 'Faturamento', valor: (u) => formatBRL(dados[u.id]?.faturamento) },
            {
              chave: 'zona',
              titulo: 'Zona',
              valor: (u) => {
                const tom = tomPorFaturamento(dados[u.id]?.faturamento, zonas)
                return <span className={`vendas-badge vendas-tone-${tom}`}>{ZONA_LABEL[tom]}</span>
              }
            }
          ]}
        />
        <h4 className="vendas-detalhe-subtitulo">Vendas que formam o faturamento</h4>
        {itens.length === 0 ? (
          <p className="vendas-empty">Nenhuma venda neste período.</p>
        ) : (
          <TabelaDetalhe
            itens={itens}
            colunas={[
              ...(mostrarVendedor ? [{ chave: 'vendedor', titulo: 'Vendedor', valor: (item) => item.vendedor }] : []),
              { chave: 'cliente', titulo: 'Cliente', valor: (item) => item.principal },
              { chave: 'telefone', titulo: 'Telefone', valor: (item) => item.secundario },
              { chave: 'pagamento', titulo: 'Pagamento', valor: (item) => item.situacao },
              { chave: 'quando', titulo: 'Cadastro', valor: (item) => item.quando },
              { chave: 'valor', titulo: 'Valor', valor: (item) => formatBRL(item.valor) }
            ]}
          />
        )}
      </>
    )
  }

  if (itens.length === 0) {
    const vazio = {
      contatos: 'Nenhum contato neste período.',
      agendadas: 'Nenhuma visita agendada neste período.',
      realizadas: 'Nenhuma visita realizada neste período.',
      faturamento: 'Nenhuma venda neste período.'
    }[detalhe.tipo]
    return <p className="vendas-empty">{vazio}</p>
  }

  if (detalhe.tipo === 'contatos') {
    return (
      <TabelaDetalhe
        itens={itens}
        colunas={[
          ...(mostrarVendedor ? [{ chave: 'vendedor', titulo: 'Vendedor', valor: (item) => item.vendedor }] : []),
          { chave: 'nome', titulo: 'Nome', valor: (item) => item.principal },
          { chave: 'telefone', titulo: 'Telefone', valor: (item) => item.secundario },
          { chave: 'situacao', titulo: 'Situação', valor: (item) => item.situacao },
          { chave: 'quando', titulo: 'Cadastro', valor: (item) => item.quando }
        ]}
      />
    )
  }

  if (detalhe.tipo === 'agendadas' || detalhe.tipo === 'realizadas') {
    return (
      <TabelaDetalhe
        itens={itens}
        colunas={[
          ...(mostrarVendedor ? [{ chave: 'vendedor', titulo: 'Vendedor', valor: (item) => item.vendedor }] : []),
          { chave: 'evento', titulo: 'Evento', valor: (item) => item.principal },
          { chave: 'quando', titulo: 'Data', valor: (item) => item.quando },
          { chave: 'obs', titulo: 'Observação', valor: (item) => item.secundario }
        ]}
      />
    )
  }

  const total = itens.reduce((soma, item) => soma + (Number(item.valor) || 0), 0)
  return (
    <>
      <TabelaDetalhe
        itens={itens}
        colunas={[
          ...(mostrarVendedor ? [{ chave: 'vendedor', titulo: 'Vendedor', valor: (item) => item.vendedor }] : []),
          { chave: 'cliente', titulo: 'Cliente', valor: (item) => item.principal },
          { chave: 'telefone', titulo: 'Telefone', valor: (item) => item.secundario },
          { chave: 'pagamento', titulo: 'Pagamento', valor: (item) => item.situacao },
          { chave: 'quando', titulo: 'Cadastro', valor: (item) => item.quando },
          { chave: 'valor', titulo: 'Valor', valor: (item) => formatBRL(item.valor) }
        ]}
      />
      <p className="vendas-detalhe-total">Total: {formatBRL(total)}</p>
    </>
  )
}

const Vendas = ({ onBack, userRole, userId, userEmpresa, permissoesMenusCache = {} }) => {
  const empresaId = userEmpresa?.id || userEmpresa
  const isEspecialista = userRole === USER_ROLES.ESPECIALISTA
  const podeConfigurar = userRole === USER_ROLES.ADMINISTRADOR || permissoesMenusCache[PERMISSAO_CONFIGURAR] === true

  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [filtroData, setFiltroData] = useState(() => mesAtualRange())
  const [especialistas, setEspecialistas] = useState([])
  const [metasPorUsuario, setMetasPorUsuario] = useState({})
  const [zonas, setZonas] = useState({ verde: 100, amarela: 50, vermelha: 0 })
  const [dados, setDados] = useState({})

  const [showConfig, setShowConfig] = useState(false)
  const [detalhe, setDetalhe] = useState(null)
  const [zonasForm, setZonasForm] = useState({ verde: '0,00', amarela: '0,00', vermelha: '0,00' })
  const [metasForm, setMetasForm] = useState({})
  const [salvando, setSalvando] = useState(false)
  const [configUsuarios, setConfigUsuarios] = useState([])
  const [incluidosSet, setIncluidosSet] = useState(() => new Set())
  const [buscaUsuario, setBuscaUsuario] = useState('')

  const dashboardRef = useRef(null)
  const dialogRef = useRef(null)
  const detalheDialogRef = useRef(null)
  const detalheReq = useRef(0)
  const [telaCheia, setTelaCheia] = useState(false)
  const salvandoRef = useRef(false)
  salvandoRef.current = salvando

  const toggleTelaCheia = async () => {
    try {
      if (!document.fullscreenElement) {
        if (dashboardRef.current?.requestFullscreen) await dashboardRef.current.requestFullscreen()
      } else if (document.exitFullscreen) {
        await document.exitFullscreen()
      }
    } catch (e) {
      setTelaCheia(prev => !prev)
    }
  }

  useEffect(() => {
    const onFsChange = () => setTelaCheia(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [])

  useEffect(() => {
    if (!showConfig) return undefined
    const dialog = dialogRef.current
    const prev = document.activeElement
    dialog?.focus()

    const onKey = (e) => {
      if (e.key === 'Escape' && !salvandoRef.current) {
        e.preventDefault()
        setShowConfig(false)
        return
      }
      if (e.key !== 'Tab' || !dialog) return
      const nodes = [...dialog.querySelectorAll('button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])')]
        .filter(el => !el.disabled && el.tabIndex !== -1)
      if (nodes.length === 0) return
      const firstEl = nodes[0]
      const lastEl = nodes[nodes.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
  }, [showConfig])

  const detalheAberto = !!detalhe
  useEffect(() => {
    if (!detalheAberto) return undefined
    const dialog = detalheDialogRef.current
    const prev = document.activeElement
    dialog?.focus()
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setDetalhe(null)
        return
      }
      if (e.key !== 'Tab' || !dialog) return
      const nodes = [...dialog.querySelectorAll('button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])')]
        .filter(el => !el.disabled && el.tabIndex !== -1)
      if (nodes.length === 0) return
      const firstEl = nodes[0]
      const lastEl = nodes[nodes.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (prev && typeof prev.focus === 'function') prev.focus()
    }
  }, [detalheAberto])

  const showNotification = (text, type) => {
    setMsg({ text, type })
    setTimeout(() => setMsg({ text: '', type: '' }), 4000)
  }

  const carregar = useCallback(async () => {
    try {
      setLoading(true)
      const inicio = filtroData.inicio || hojeStr()
      const fimBase = filtroData.fim || filtroData.inicio || hojeStr()
      const fimExclusivo = proximoDiaStr(fimBase)

      const { rows: metasData, colunaNova } = await buscarMetas()

      let usuariosQuery = supabase
        .from('usuarios_sistema')
        .select('id, nome, role, exibir_em_vendas')
        .eq('status', 'ativo')
        .order('nome', { ascending: true })
      usuariosQuery = colunaNova
        ? usuariosQuery.eq('exibir_em_vendas', true)
        : usuariosQuery.or('role.eq.especialista,exibir_em_vendas.eq.true')
      const { data: usuarios, error: errUsuarios } = await usuariosQuery
      if (errUsuarios) throw errUsuarios

      const lista = isEspecialista
        ? (usuarios || []).filter(u => String(u.id) === String(userId))
        : (usuarios || [])
      setEspecialistas(lista)
      const ids = lista.map(u => u.id)

      const { data: zonasData } = await supabase
        .from('metas_zonas')
        .select('verde, amarela, vermelha')
        .order('id', { ascending: true })
        .limit(1)
      if (zonasData && zonasData[0]) {
        setZonas({
          verde: Number(zonasData[0].verde),
          amarela: Number(zonasData[0].amarela),
          vermelha: Number(zonasData[0].vermelha)
        })
        setZonasForm({
          verde: moneyToInput(zonasData[0].verde),
          amarela: moneyToInput(zonasData[0].amarela),
          vermelha: moneyToInput(zonasData[0].vermelha)
        })
      }

      const metasMap = {}
      for (const m of metasData || []) metasMap[m.id_usuario] = m
      setMetasPorUsuario(metasMap)

      const agregados = {}
      ids.forEach(id => { agregados[id] = { contatos: 0, agendadas: 0, realizadas: 0, faturamento: 0 } })

      if (ids.length > 0) {
        const { data: leadsData } = await supabase
          .from('leads')
          .select('responsavel')
          .in('responsavel', ids)
          .gte('created_at', inicio)
          .lt('created_at', fimExclusivo)
        for (const l of leadsData || []) {
          if (agregados[l.responsavel]) agregados[l.responsavel].contatos += 1
        }

        const { data: eventosData } = await supabase
          .from('eventos_calendario')
          .select('id_responsavel, executado')
          .in('id_responsavel', ids)
          .gte('data_hora', inicio)
          .lt('data_hora', fimExclusivo)
        for (const ev of eventosData || []) {
          const ag = agregados[ev.id_responsavel]
          if (!ag) continue
          if (ev.executado) ag.realizadas += 1
          else ag.agendadas += 1
        }

        const { data: clientesData } = await supabase
          .from('clientes_cadastro')
          .select('id_responsavel, valor_total')
          .in('id_responsavel', ids)
          .gte('data_cadastro', inicio)
          .lt('data_cadastro', fimExclusivo)
        for (const c of clientesData || []) {
          if (agregados[c.id_responsavel]) agregados[c.id_responsavel].faturamento += Number(c.valor_total) || 0
        }
      }

      setDados(agregados)
    } catch (err) {
      console.error('Erro ao carregar painel de vendas:', err)
      showNotification(err.message || 'Erro ao carregar dados.', 'error')
    } finally {
      setLoading(false)
    }
  }, [filtroData.inicio, filtroData.fim, isEspecialista, userId])

  useEffect(() => {
    carregar()
  }, [carregar])

  const totais = useMemo(() => {
    return especialistas.reduce((acc, u) => {
      const linha = dados[u.id] || { contatos: 0, agendadas: 0, realizadas: 0, faturamento: 0 }
      acc.contatos += linha.contatos
      acc.agendadas += linha.agendadas
      acc.realizadas += linha.realizadas
      acc.faturamento += linha.faturamento
      return acc
    }, { contatos: 0, agendadas: 0, realizadas: 0, faturamento: 0 })
  }, [especialistas, dados])

  const periodoLabel = filtroData.inicio && filtroData.fim
    ? (filtroData.inicio === filtroData.fim
      ? formatarDataCurta(filtroData.inicio)
      : `${formatarDataCurta(filtroData.inicio)} a ${formatarDataCurta(filtroData.fim)}`)
    : 'Período atual'

  const abrirDetalhe = async (tipo, usuario) => {
    const req = ++detalheReq.current
    setDetalhe({ tipo, usuario: usuario || null, itens: [], carregando: true, erro: '' })
    const inicio = filtroData.inicio || hojeStr()
    const fimExclusivo = proximoDiaStr(filtroData.fim || filtroData.inicio || hojeStr())
    const ids = usuario ? [usuario.id] : especialistas.map(u => u.id)
    const nomes = Object.fromEntries(especialistas.map(u => [String(u.id), u.nome || 'Sem nome']))
    if (ids.length === 0) {
      if (detalheReq.current === req) setDetalhe(prev => prev && { ...prev, carregando: false, itens: [] })
      return
    }
    try {
      let itens = []
      if (tipo === 'contatos') {
        const { data, error } = await supabase
          .from('leads')
          .select('id, nome, telefone, status, created_at, responsavel')
          .in('responsavel', ids)
          .gte('created_at', inicio)
          .lt('created_at', fimExclusivo)
          .order('created_at', { ascending: false })
        if (error) throw error
        itens = (data || []).map(l => ({
          id: l.id,
          vendedorId: l.responsavel,
          vendedor: nomes[String(l.responsavel)] || '—',
          principal: l.nome || 'Sem nome',
          secundario: l.telefone || '—',
          situacao: STATUS_LEAD[l.status] || (l.status ? String(l.status).replace(/_/g, ' ') : '—'),
          quando: formatarDataHora(l.created_at)
        }))
      } else if (tipo === 'agendadas' || tipo === 'realizadas') {
        const { data, error } = await supabase
          .from('eventos_calendario')
          .select('id, nome_evento, data_hora, observacao, id_responsavel')
          .in('id_responsavel', ids)
          .eq('executado', tipo === 'realizadas')
          .gte('data_hora', inicio)
          .lt('data_hora', fimExclusivo)
          .order('data_hora', { ascending: true })
        if (error) throw error
        itens = (data || []).map(ev => ({
          id: ev.id,
          vendedorId: ev.id_responsavel,
          vendedor: nomes[String(ev.id_responsavel)] || '—',
          principal: ev.nome_evento || 'Sem nome',
          secundario: ev.observacao || '—',
          quando: formatarDataHora(ev.data_hora)
        }))
      } else {
        const { data, error } = await supabase
          .from('clientes_cadastro')
          .select('id, nome_completo, telefone, valor_total, data_cadastro, tipo_pagamento, id_responsavel')
          .in('id_responsavel', ids)
          .gte('data_cadastro', inicio)
          .lt('data_cadastro', fimExclusivo)
          .order('data_cadastro', { ascending: false })
        if (error) throw error
        itens = (data || []).map(c => ({
          id: c.id,
          vendedorId: c.id_responsavel,
          vendedor: nomes[String(c.id_responsavel)] || '—',
          principal: c.nome_completo || 'Sem nome',
          secundario: c.telefone || '—',
          situacao: PAGAMENTO_LABEL[c.tipo_pagamento] || c.tipo_pagamento || '—',
          quando: formatarDataHora(c.data_cadastro),
          valor: Number(c.valor_total) || 0
        }))
      }
      if (detalheReq.current !== req) return
      setDetalhe(prev => prev && { ...prev, carregando: false, itens })
    } catch (err) {
      if (detalheReq.current !== req) return
      setDetalhe(prev => prev && { ...prev, carregando: false, erro: err.message || 'Erro ao carregar os detalhes.' })
    }
  }

  const abrirConfig = async () => {
    setBuscaUsuario('')
    setShowConfig(true)

    try {
      const { data: zonasData } = await supabase
        .from('metas_zonas')
        .select('verde, amarela, vermelha')
        .order('id', { ascending: true })
        .limit(1)
      if (zonasData && zonasData[0]) {
        setZonasForm({
          verde: moneyToInput(zonasData[0].verde),
          amarela: moneyToInput(zonasData[0].amarela),
          vermelha: moneyToInput(zonasData[0].vermelha)
        })
      } else {
        setZonasForm({
          verde: moneyToInput(zonas.verde),
          amarela: moneyToInput(zonas.amarela),
          vermelha: moneyToInput(zonas.vermelha)
        })
      }

      const { rows: metasData } = await buscarMetas()
      const metasMap = {}
      for (const m of metasData || []) metasMap[m.id_usuario] = m
      setMetasPorUsuario(metasMap)

      let usuariosQuery = supabase
        .from('usuarios_sistema')
        .select('id, nome, role, exibir_em_vendas')
        .eq('status', 'ativo')
        .order('nome', { ascending: true })
      if (empresaId) usuariosQuery = usuariosQuery.eq('empresa_id', empresaId)
      const { data, error } = await usuariosQuery
      if (error) throw error

      const lista = data || []
      setConfigUsuarios(lista)
      setIncluidosSet(new Set(lista.filter(u => u.exibir_em_vendas).map(u => u.id)))
      const formMetas = {}
      lista.forEach(u => {
        formMetas[u.id] = metasDoRegistro(metasMap[u.id])
      })
      setMetasForm(formMetas)
    } catch (err) {
      console.error('Erro ao carregar configurações:', err)
      showNotification('Erro ao carregar configurações salvas.', 'error')
    }
  }

  const toggleIncluido = (id) => {
    setIncluidosSet(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const atualizarMeta = (id, campo, valor) => {
    setMetasForm(prev => ({
      ...prev,
      [id]: { ...(prev[id] || metasVazias()), [campo]: valor }
    }))
  }

  const salvarConfig = async (e) => {
    e?.preventDefault()
    const verde = parseMoney(zonasForm.verde)
    const amarela = parseMoney(zonasForm.amarela)
    const vermelha = parseMoney(zonasForm.vermelha)
    if (verde < amarela || amarela < vermelha) {
      showNotification('A faixa verde precisa ser maior ou igual à amarela, e a amarela maior ou igual à vermelha.', 'error')
      return
    }

    try {
      setSalvando(true)

      const { data: zonaExistente, error: errZonaBusca } = await supabase
        .from('metas_zonas')
        .select('id')
        .order('id', { ascending: true })
        .limit(1)
      if (errZonaBusca) throw errZonaBusca
      if (zonaExistente && zonaExistente[0]) {
        const { error } = await supabase.from('metas_zonas').update({ verde, amarela, vermelha }).eq('id', zonaExistente[0].id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('metas_zonas').insert({ verde, amarela, vermelha })
        if (error) throw error
      }

      const rows = configUsuarios
        .filter(u => incluidosSet.has(u.id))
        .map(u => ({
          id_usuario: u.id,
          meta_contatos: Math.max(0, parseInt(metasForm[u.id]?.meta_contatos, 10) || 0),
          meta_visitas_agendadas: Math.max(0, parseInt(metasForm[u.id]?.meta_visitas_agendadas, 10) || 0),
          meta_visitas_realizadas: Math.max(0, parseInt(metasForm[u.id]?.meta_visitas_realizadas, 10) || 0),
          meta_faturamento: Math.max(0, parseMoney(metasForm[u.id]?.meta_faturamento))
        }))
      if (rows.length > 0) {
        const { error } = await supabase.from('especialista_metas').upsert(rows, { onConflict: 'id_usuario' })
        if (error) {
          if (/meta_faturamento/i.test(error.message || '')) {
            throw new Error('Falta a coluna de meta de faturamento. Execute o script database/172_painel_atividade_metas_e_acesso.sql no banco e tente de novo.')
          }
          throw error
        }
      }

      const paraIncluir = []
      const paraExcluir = []
      configUsuarios.forEach(u => {
        const desejado = incluidosSet.has(u.id)
        if (desejado && !u.exibir_em_vendas) paraIncluir.push(u.id)
        if (!desejado && u.exibir_em_vendas) paraExcluir.push(u.id)
      })
      if (paraIncluir.length > 0) {
        const { error } = await supabase.from('usuarios_sistema').update({ exibir_em_vendas: true }).in('id', paraIncluir)
        if (error) throw error
      }
      if (paraExcluir.length > 0) {
        const { error } = await supabase.from('usuarios_sistema').update({ exibir_em_vendas: false }).in('id', paraExcluir)
        if (error) throw error
      }

      showNotification('Metas salvas com sucesso.', 'success')
      setShowConfig(false)
      carregar()
    } catch (err) {
      console.error('Erro ao salvar metas:', err)
      showNotification('Erro ao salvar metas: ' + (err.message || 'erro desconhecido'), 'error')
    } finally {
      setSalvando(false)
    }
  }

  const usuariosFiltrados = configUsuarios.filter(u =>
    (u.nome || '').toLowerCase().includes(buscaUsuario.trim().toLowerCase())
  )
  const usuariosNoPainel = configUsuarios.filter(u => incluidosSet.has(u.id))

  return (
    <div className={`vendas-root${telaCheia ? ' vendas-fullscreen' : ''}`} ref={dashboardRef}>
      <header className="vendas-header">
        <div className="vendas-header-title">
          {onBack && (
            <button type="button" className="vendas-btn outline" onClick={onBack}>Voltar</button>
          )}
          <div>
            <h2>Painel de atividade diária</h2>
            <p>{periodoLabel}</p>
          </div>
        </div>
        <div className="vendas-header-actions" hidden={telaCheia}>
          <div className="vendas-filtro-datas">
            <label htmlFor="vendas-inicio">Início</label>
            <input
              id="vendas-inicio"
              type="date"
              value={filtroData.inicio}
              max={filtroData.fim || undefined}
              onChange={(e) => setFiltroData(prev => ({ ...prev, inicio: e.target.value }))}
            />
            <label htmlFor="vendas-fim">Fim</label>
            <input
              id="vendas-fim"
              type="date"
              value={filtroData.fim}
              min={filtroData.inicio || undefined}
              onChange={(e) => setFiltroData(prev => ({ ...prev, fim: e.target.value }))}
            />
          </div>
          <button type="button" className="vendas-btn outline" onClick={carregar} disabled={loading}>
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
          <button type="button" className="vendas-btn outline" onClick={toggleTelaCheia}>Tela cheia</button>
          {podeConfigurar && (
            <button type="button" className="vendas-btn" onClick={abrirConfig}>Configurar metas</button>
          )}
        </div>
      </header>

      {telaCheia && (
        <button type="button" className="vendas-btn-sair" onClick={toggleTelaCheia}>Sair da tela cheia</button>
      )}

      {msg.text && (
        <div className={`vendas-msg vendas-msg-${msg.type}`} role={msg.type === 'error' ? 'alert' : 'status'}>
          {msg.text}
        </div>
      )}

      <section className="vendas-kpis" aria-label="Totais do período">
        <article className="vendas-kpi">
          <span>Contatos</span>
          <strong>{totais.contatos}</strong>
        </article>
        <article className="vendas-kpi">
          <span>Visitas agendadas</span>
          <strong>{totais.agendadas}</strong>
        </article>
        <article className="vendas-kpi">
          <span>Visitas realizadas</span>
          <strong>{totais.realizadas}</strong>
        </article>
        <article className="vendas-kpi">
          <span>Faturamento</span>
          <strong>{formatBRL(totais.faturamento)}</strong>
        </article>
      </section>

      <div className="vendas-tabela-wrap">
        <table className="vendas-tabela">
          <caption className="vendas-sr">Atividade dos vendedores no período selecionado, comparada com a meta de cada coluna.</caption>
          <thead>
            <tr>
              <th scope="col">Vendedor</th>
              <th scope="col">
                <button type="button" className="vendas-col-btn" disabled={loading} onClick={() => abrirDetalhe('contatos', null)}>Contatos</button>
              </th>
              <th scope="col">
                <button type="button" className="vendas-col-btn" disabled={loading} onClick={() => abrirDetalhe('agendadas', null)}>Visitas agendadas</button>
              </th>
              <th scope="col">
                <button type="button" className="vendas-col-btn" disabled={loading} onClick={() => abrirDetalhe('realizadas', null)}>Visitas realizadas</button>
              </th>
              <th scope="col">
                <button type="button" className="vendas-col-btn" disabled={loading} onClick={() => abrirDetalhe('faturamento', null)}>Faturamento do dia</button>
              </th>
              <th scope="col">
                <button type="button" className="vendas-col-btn" disabled={loading} onClick={() => abrirDetalhe('zona', null)}>Zona</button>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="vendas-empty">Carregando…</td></tr>
            ) : especialistas.length === 0 ? (
              <tr><td colSpan={6} className="vendas-empty">Nenhum vendedor selecionado para este painel.</td></tr>
            ) : (
              especialistas.map(u => {
                const linha = dados[u.id] || { contatos: 0, agendadas: 0, realizadas: 0, faturamento: 0 }
                const metas = metasPorUsuario[u.id] || {}
                const zonaTom = tomPorFaturamento(linha.faturamento, zonas)
                return (
                  <tr key={u.id}>
                    <th scope="row">
                      <div className="vendas-vendedor">
                        <span className="vendas-avatar" aria-hidden="true">{iniciais(u.nome)}</span>
                        <span>{u.nome || 'Sem nome'}</span>
                      </div>
                    </th>
                    <td>
                      <button type="button" className="vendas-abrir" onClick={() => abrirDetalhe('contatos', u)} aria-label={`Ver contatos de ${u.nome || 'vendedor'}`}>
                        <CelulaMeta valor={linha.contatos} meta={metas.meta_contatos} tom={tomPorMeta(linha.contatos, metas.meta_contatos)} />
                      </button>
                    </td>
                    <td>
                      <button type="button" className="vendas-abrir" onClick={() => abrirDetalhe('agendadas', u)} aria-label={`Ver visitas agendadas de ${u.nome || 'vendedor'}`}>
                        <CelulaMeta valor={linha.agendadas} meta={metas.meta_visitas_agendadas} tom={tomPorMeta(linha.agendadas, metas.meta_visitas_agendadas)} />
                      </button>
                    </td>
                    <td>
                      <button type="button" className="vendas-abrir" onClick={() => abrirDetalhe('realizadas', u)} aria-label={`Ver visitas realizadas de ${u.nome || 'vendedor'}`}>
                        <CelulaMeta valor={linha.realizadas} meta={metas.meta_visitas_realizadas} tom={tomPorMeta(linha.realizadas, metas.meta_visitas_realizadas)} />
                      </button>
                    </td>
                    <td>
                      <button type="button" className="vendas-abrir" onClick={() => abrirDetalhe('faturamento', u)} aria-label={`Ver faturamento de ${u.nome || 'vendedor'}`}>
                        <CelulaMeta valor={linha.faturamento} meta={metas.meta_faturamento} tom={tomPorMeta(linha.faturamento, metas.meta_faturamento)} dinheiro />
                      </button>
                    </td>
                    <td>
                      <button type="button" className="vendas-abrir" onClick={() => abrirDetalhe('zona', u)} aria-label={`Ver zona de ${u.nome || 'vendedor'}`}>
                        <span className={`vendas-badge vendas-tone-${zonaTom}`}>{ZONA_LABEL[zonaTom]}</span>
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <p className="vendas-legenda">
        Verde a partir da meta. Amarelo a partir de 50%. A coluna Zona usa o faturamento do período:
        verde desde {formatBRL(zonas.verde)}, amarela desde {formatBRL(zonas.amarela)} e vermelha abaixo disso.
      </p>

      {detalhe && (
        <div className="vendas-modal-overlay" onClick={() => setDetalhe(null)}>
          <div
            className="vendas-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vendas-detalhe-titulo"
            ref={detalheDialogRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vendas-modal-header">
              <div>
                <h3 id="vendas-detalhe-titulo">{TITULO_COLUNA[detalhe.tipo]}</h3>
                <p className="vendas-hint">
                  {detalhe.usuario ? (detalhe.usuario.nome || 'Vendedor') : 'Todos os vendedores do painel'} · {periodoLabel}
                </p>
              </div>
              <button type="button" className="vendas-btn outline" onClick={() => setDetalhe(null)}>Fechar</button>
            </div>
            <div className="vendas-modal-body">
              {detalhe.carregando ? (
                <p className="vendas-empty">Carregando…</p>
              ) : detalhe.erro ? (
                <p className="vendas-msg vendas-msg-error" role="alert">{detalhe.erro}</p>
              ) : (
                <DetalheColuna
                  detalhe={detalhe}
                  zonas={zonas}
                  dados={dados}
                  vendedores={detalhe.usuario ? [detalhe.usuario] : especialistas}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showConfig && (
        <div className="vendas-modal-overlay">
          <div
            className="vendas-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vendas-config-titulo"
            ref={dialogRef}
            tabIndex={-1}
          >
            <div className="vendas-modal-header">
              <h3 id="vendas-config-titulo">Configurar metas</h3>
              <button type="button" className="vendas-btn outline vendas-modal-close" onClick={() => !salvando && setShowConfig(false)} aria-label="Fechar">
                Fechar
              </button>
            </div>
            <form onSubmit={salvarConfig}>
              <div className="vendas-modal-body">
                <section>
                  <h4>Zona</h4>
                  <p className="vendas-hint">Faturamento mínimo do período para cada faixa. Verde é a faixa mais alta.</p>
                  <div className="vendas-zonas-grid">
                    <div className="vendas-field">
                      <label htmlFor="zona-verde">Verde (R$)</label>
                      <input id="zona-verde" type="text" inputMode="numeric" value={zonasForm.verde} onChange={(e) => setZonasForm(p => ({ ...p, verde: formatMoneyInput(e.target.value) }))} />
                    </div>
                    <div className="vendas-field">
                      <label htmlFor="zona-amarela">Amarela (R$)</label>
                      <input id="zona-amarela" type="text" inputMode="numeric" value={zonasForm.amarela} onChange={(e) => setZonasForm(p => ({ ...p, amarela: formatMoneyInput(e.target.value) }))} />
                    </div>
                    <div className="vendas-field">
                      <label htmlFor="zona-vermelha">Vermelha (R$)</label>
                      <input id="zona-vermelha" type="text" inputMode="numeric" value={zonasForm.vermelha} onChange={(e) => setZonasForm(p => ({ ...p, vermelha: formatMoneyInput(e.target.value) }))} />
                    </div>
                  </div>
                </section>

                <section>
                  <h4>Quem aparece na coluna Vendedor</h4>
                  <p className="vendas-hint">
                    {usuariosNoPainel.length} de {configUsuarios.length} usuários ativos desta empresa entram no painel.
                  </p>
                  <label className="vendas-sr" htmlFor="vendas-busca-usuario">Buscar usuário</label>
                  <input
                    id="vendas-busca-usuario"
                    type="search"
                    className="vendas-busca"
                    placeholder="Buscar usuário"
                    value={buscaUsuario}
                    onChange={(e) => setBuscaUsuario(e.target.value)}
                  />
                  <div className="vendas-usuarios-lista">
                    {usuariosFiltrados.length === 0 ? (
                      <p className="vendas-empty">Nenhum usuário encontrado.</p>
                    ) : (
                      usuariosFiltrados.map(u => (
                        <label key={u.id} className="vendas-usuario-item">
                          <input
                            type="checkbox"
                            checked={incluidosSet.has(u.id)}
                            onChange={() => toggleIncluido(u.id)}
                          />
                          <span>{u.nome || 'Sem nome'}</span>
                          <small>{security.getRoleDisplayName(u.role)}</small>
                        </label>
                      ))
                    )}
                  </div>
                </section>

                <section>
                  <h4>Metas de quem aparece no painel</h4>
                  <p className="vendas-hint">Contatos, visitas e faturamento são a meta de cada vendedor no período.</p>
                  {usuariosNoPainel.length === 0 ? (
                    <p className="vendas-empty">Marque ao menos um usuário para definir metas.</p>
                  ) : (
                    <div className="vendas-metas-table-wrap">
                      <table className="vendas-metas-table">
                        <thead>
                          <tr>
                            <th scope="col">Vendedor</th>
                            <th scope="col">Contatos</th>
                            <th scope="col">Visitas agendadas</th>
                            <th scope="col">Visitas realizadas</th>
                            <th scope="col">Faturamento (R$)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usuariosNoPainel.map(u => (
                            <tr key={u.id}>
                              <th scope="row">{u.nome || 'Sem nome'}</th>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  aria-label={`Meta de contatos de ${u.nome || 'vendedor'}`}
                                  value={metasForm[u.id]?.meta_contatos ?? '0'}
                                  onChange={(e) => atualizarMeta(u.id, 'meta_contatos', e.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  aria-label={`Meta de visitas agendadas de ${u.nome || 'vendedor'}`}
                                  value={metasForm[u.id]?.meta_visitas_agendadas ?? '0'}
                                  onChange={(e) => atualizarMeta(u.id, 'meta_visitas_agendadas', e.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  aria-label={`Meta de visitas realizadas de ${u.nome || 'vendedor'}`}
                                  value={metasForm[u.id]?.meta_visitas_realizadas ?? '0'}
                                  onChange={(e) => atualizarMeta(u.id, 'meta_visitas_realizadas', e.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  aria-label={`Meta de faturamento de ${u.nome || 'vendedor'}`}
                                  value={metasForm[u.id]?.meta_faturamento ?? ''}
                                  onChange={(e) => atualizarMeta(u.id, 'meta_faturamento', formatMoneyInput(e.target.value))}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              </div>
              <div className="vendas-modal-footer">
                <button type="button" className="vendas-btn outline" onClick={() => setShowConfig(false)} disabled={salvando}>Cancelar</button>
                <button type="submit" className="vendas-btn" disabled={salvando}>
                  {salvando ? 'Salvando…' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Vendas
